import React, { useId, useRef, useState } from 'react';
import { useCalcAmountDisplayMode } from './useCalcAmountDisplayMode';
import { computeTooltipPlacement } from './tooltipPositioning';
import './CalculatorPrecisionToggle.css';

/** ≈ — switch back to readable rounded amounts */
function RoundedIcon() {
  return (
    <svg
      className="calc-precision-toggle__icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <text
        x="12"
        y="17.5"
        textAnchor="middle"
        fontSize="18"
        fontWeight="700"
        fontFamily="Georgia, 'Times New Roman', serif"
      >
        ≈
      </text>
    </svg>
  );
}

/** Exact digits — switch to precise rupee amounts */
function PreciseIcon() {
  return (
    <svg
      className="calc-precision-toggle__icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <text
        x="12"
        y="16.5"
        textAnchor="middle"
        fontSize="11"
        fontWeight="800"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
        letterSpacing="-0.4"
      >
        1.0
      </text>
    </svg>
  );
}

/**
 * Tiny control matching the result-panel “?” size.
 * Shows the alternate mode only (Rounded → Precise icon, Precise → Rounded icon).
 */
export default function CalculatorPrecisionToggle({ className = '' }) {
  const { mode, toggle } = useCalcAmountDisplayMode();
  const tooltipId = useId();
  const tooltipWrapRef = useRef(null);
  const [tooltipPosClass, setTooltipPosClass] = useState('');
  const [tooltipInlineStyle, setTooltipInlineStyle] = useState(undefined);

  const showingRounded = mode !== 'precise';
  const nextLabel = showingRounded ? 'Precise' : 'Rounded';
  const tooltip = showingRounded
    ? 'Amounts are rounded for easier reading. Click to show precise (exact) rupees.'
    : 'Showing precise amounts. Click to show rounded figures for easier reading.';

  function updateTooltipPosition() {
    const placement = computeTooltipPlacement(tooltipWrapRef.current);
    setTooltipPosClass(placement.tooltipPosClass);
    setTooltipInlineStyle(placement.tooltipInlineStyle);
  }

  return (
    <span
      ref={tooltipWrapRef}
      className={`calc-precision-toggle nps-ups-tooltip-wrap ${tooltipPosClass}${
        className ? ` ${className}` : ''
      }`.trim()}
    >
      <button
        type="button"
        className="btn-small-round calc-precision-toggle__btn"
        aria-describedby={tooltipId}
        aria-label={`Switch to ${nextLabel} amounts`}
        title={tooltip}
        onClick={toggle}
        onMouseEnter={updateTooltipPosition}
        onFocus={updateTooltipPosition}
      >
        {showingRounded ? <PreciseIcon /> : <RoundedIcon />}
      </button>
      <span
        className="nps-ups-tooltip-bubble"
        id={tooltipId}
        role="tooltip"
        style={tooltipInlineStyle}
      >
        {tooltip}
      </span>
    </span>
  );
}
